import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { supabaseClient } from '../../services/supabase';
import { useLocation } from '../../contexts/LocationContext';
import { ChartLayout, OverviewContent, MethodologyContent, RelevanceContent, EquityContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PVInstallerData {
  installer: string;
  total_kw: number;
  total_cost: number;
  total_projects: number;
  cost_per_kw: number;
}

interface ChartMetrics {
  totalInstallers: number;
  totalKW: number;
  totalCost: number;
  avgCostPerKW: number;
  totalProjects: number;
}

interface GeoidPVChartProps {
  onMetricsUpdate?: (metrics: ChartMetrics) => void;
}

const GeoidPVChart: React.FC<GeoidPVChartProps> = ({ onMetricsUpdate }) => {
  const { selectedGeofips, selectedLocation } = useLocation();
  const [data, setData] = useState<PVInstallerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState('overview');

  // Calculate metrics from data
  const calculateMetrics = (installerData: PVInstallerData[]): ChartMetrics => {
    const totalInstallers = installerData.length;
    const totalKW = installerData.reduce((sum, item) => sum + item.total_kw, 0);
    const totalCost = installerData.reduce((sum, item) => sum + item.total_cost, 0);
    const totalProjects = installerData.reduce((sum, item) => sum + item.total_projects, 0);
    const avgCostPerKW = totalKW > 0 ? totalCost / totalKW : 0;

    return {
      totalInstallers,
      totalKW: Math.round(totalKW),
      totalCost: Math.round(totalCost),
      avgCostPerKW: Math.round(avgCostPerKW * 100) / 100,
      totalProjects: Math.round(totalProjects)
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedGeofips) {
        setData([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`Fetching PV data for geoid: ${selectedGeofips} (${selectedLocation})`);

        const { data: pvData, error: pvError } = await supabaseClient
          .from('pv_test_ef')
          .select('installer, total_kw, total_cost, total_projects, cost_per_kw')
          .eq('geoid', selectedGeofips)
          .not('installer', 'is', null)
          .not('total_kw', 'is', null);

        if (pvError) {
          throw pvError;
        }

        if (pvData) {
          // Group by installer and sum values
          const groupedData = pvData.reduce((acc: Record<string, PVInstallerData>, row) => {
            const installer = row.installer;
            const kw = row.total_kw || 0;
            const cost = row.total_cost || 0;
            const projects = row.total_projects || 0;
            const costPerKw = row.cost_per_kw || 0;
            
            if (acc[installer]) {
              acc[installer].total_kw += kw;
              acc[installer].total_cost += cost;
              acc[installer].total_projects += projects;
              // Recalculate average cost per KW
              acc[installer].cost_per_kw = acc[installer].total_cost / acc[installer].total_kw;
            } else {
              acc[installer] = {
                installer,
                total_kw: kw,
                total_cost: cost,
                total_projects: projects,
                cost_per_kw: costPerKw
              };
            }
            
            return acc;
          }, {});

          // Convert to array and sort by total_kw descending
          const sortedData = Object.values(groupedData)
            .map(item => ({
              ...item,
              total_kw: Math.round(item.total_kw),
              total_cost: Math.round(item.total_cost),
              total_projects: Math.round(item.total_projects),
              cost_per_kw: Math.round(item.cost_per_kw)
            }))
            .sort((a, b) => b.total_kw - a.total_kw)
            .slice(0, 10); // Top 10

          setData(sortedData);
          
          // Calculate and send metrics to parent component
          const metrics = calculateMetrics(sortedData);
          if (onMetricsUpdate) {
            onMetricsUpdate(metrics);
          }
        }
      } catch (err) {
        console.error('Error fetching PV data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedGeofips, selectedLocation]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#666'
      }}>
        Loading PV data for {selectedLocation || 'selected location'}...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#dc2626',
        textAlign: 'center',
        padding: '20px'
      }}>
        Error loading data: {error}
      </div>
    );
  }

  if (!selectedGeofips) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#666',
        textAlign: 'center',
        padding: '20px'
      }}>
        Please select a location to view PV installation data
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#666',
        textAlign: 'center',
        padding: '20px'
      }}>
        No PV installation data found for {selectedLocation} (Geoid: {selectedGeofips})
      </div>
    );
  }

  // Generate colors for different installers
  const colors = [
    'rgba(79, 70, 229, 0.8)',   // Indigo
    'rgba(16, 185, 129, 0.8)',  // Emerald
    'rgba(245, 158, 11, 0.8)',  // Amber
    'rgba(239, 68, 68, 0.8)',   // Red
    'rgba(139, 92, 246, 0.8)',  // Violet
    'rgba(236, 72, 153, 0.8)',  // Pink
    'rgba(14, 165, 233, 0.8)',  // Sky
    'rgba(34, 197, 94, 0.8)',   // Green
    'rgba(251, 146, 60, 0.8)',  // Orange
    'rgba(168, 85, 247, 0.8)'   // Purple
  ];

  const chartData = {
    labels: data.map(item => item.installer),
    datasets: [
      {
        label: 'Total KW Installed',
        data: data.map(item => item.total_kw),
        backgroundColor: colors.slice(0, data.length),
        borderColor: colors.slice(0, data.length).map(color => color.replace('0.8', '1')),
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const, // Horizontal bars
    plugins: {
      legend: {
        position: 'top' as const,
        display: false, // Hide legend since we have colored bars
      },
      title: {
        display: true,
        text: `Top Solar PV Installers in ${selectedLocation}`,
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const dataIndex = context.dataIndex;
            const item = data[dataIndex];
            return [
              `Total KW: ${item.total_kw.toLocaleString()}`,
              `Total Cost: $${item.total_cost.toLocaleString()}`,
              `Projects: ${item.total_projects.toLocaleString()}`,
              `Cost per KW: $${item.cost_per_kw.toLocaleString()}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Total KW Installed',
        },
        ticks: {
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Installer',
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
        }
      },
    },
  };

  return (
    <ChartLayout
      title="Solar PV Installers by Location"
      description="Location-specific analysis of solar photovoltaic installation companies, showing market concentration, capacity deployment, and cost trends for climate action planning."
      chartType="bar"
      layout="vertical"
      activeView={activeView}
      onViewChange={setActiveView}
      keyMetrics={
        <KeyMetrics>
          <KeyMetric
            value={data.length.toString()}
            label="Top Installers Shown"
            color="#1e3a8a"
          />
          <KeyMetric
            value={`${(data.reduce((sum, item) => sum + item.total_kw, 0) / 1000).toFixed(0)}K`}
            label="Total KW Capacity"
            color="#059669"
          />
          <KeyMetric
            value={`$${(data.reduce((sum, item) => sum + item.total_cost, 0) / 1000000).toFixed(1)}M`}
            label="Total Investment"
            color="#7c3aed"
          />
          <KeyMetric
            value={`$${data.length > 0 ? (data.reduce((sum, item) => sum + item.total_cost, 0) / data.reduce((sum, item) => sum + item.total_kw, 0)).toFixed(1) : '0'}`}
            label="Avg Cost per KW"
            color="#f59e0b"
          />
        </KeyMetrics>
      }
      chartContent={
        <div style={{ width: '100%', height: '400px' }}>
          <Bar data={chartData} options={options} />
        </div>
      }
      overviewContent={
        <OverviewContent
          title="Solar Installation Market Analysis"
          description="This chart reveals the solar installation landscape for your selected location, showing which companies are driving renewable energy deployment and their relative market share."
        >
          <p>
            The horizontal bar chart displays the top solar PV installers ranked by total kilowatt (KW) capacity installed. 
            Each bar represents an installer's cumulative solar deployment, with color coding for easy identification.
          </p>
          <p>
            <strong>Key Insights:</strong> Market concentration varies significantly by location. Some areas show 
            dominant players with large market shares, while others have more distributed installation activity 
            across multiple companies.
          </p>
          <p>
            <strong>Interactive Features:</strong> Hover over bars to see detailed metrics including total cost, 
            number of projects, and cost per KW. The chart updates automatically when you change locations 
            using the app's location selector.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action Planning"
          description="Understanding local solar installation markets directly informs renewable energy policy, workforce development, and community engagement strategies."
        >
          <div className="methodology-details-simple">
            <h5>Policy Development</h5>
            <p>
              Identifies key industry partners for policy implementation and reveals market gaps 
              where targeted incentives could stimulate additional solar deployment. Understanding 
              installer capacity helps set realistic renewable energy targets.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Workforce Development</h5>
            <p>
              Shows which companies are creating local green jobs and where workforce training 
              programs could have the greatest impact. Large installers often offer apprenticeship 
              opportunities and career pathways in clean energy.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Community Engagement</h5>
            <p>
              Reveals trusted local partners for community solar programs and identifies 
              companies with experience in diverse neighborhoods. This supports equitable 
              clean energy access and community ownership models.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations in Solar Deployment"
          description="Solar installation patterns reveal important equity implications for clean energy access, economic opportunity, and community benefits."
        >
          <div className="methodology-details-simple">
            <h5>Geographic Distribution</h5>
            <p>
              Market concentration in certain areas may indicate barriers to solar access in 
              underserved communities. Areas with fewer installers or higher costs per KW 
              may need targeted support programs and community outreach.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Economic Opportunity</h5>
            <p>
              Large-scale installers often provide better wages and benefits, while smaller 
              local companies may offer more accessible entry points for diverse workers. 
              Understanding this balance helps design inclusive workforce development programs.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Community Benefits</h5>
            <p>
              Local installers may be more responsive to community needs and more likely 
              to hire locally. Supporting diverse installer ecosystems can ensure that 
              clean energy investments create local economic benefits and build community wealth.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Collection and Analysis Methodology"
          description="This analysis uses comprehensive solar installation data to provide location-specific insights into renewable energy market dynamics."
          dataSources={[
            'Supabase pv_test_ef table - Solar installation records'
          ]}
          methodology={[
            'Filter installation records by current location geoid for geographic accuracy',
            'Group records by installer name to aggregate company-level metrics',
            'Sum total_kw, total_cost, and total_projects per installer',
            'Calculate weighted average cost per KW for market analysis',
            'Sort installers by total KW capacity in descending order',
            'Display top installers as horizontal bar chart for clarity',
            'Update dynamically when location selection changes'
          ]}
        />
      }
      showRelevanceEquity={true}
    />
  );
};

export default GeoidPVChart;
